import type { MediaRef } from './media.js';

export type ConversationStatus = 'active' | 'ended' | 'blocked';

export interface ConversationDoc {
  id: string;
  /** Exactly two members after mutual-consent introduction. */
  members: string[];
  introductionId?: string;
  status: ConversationStatus;
  createdAtISO: string;
  lastMessageAtISO?: string;
  lastMessagePreview?: string;
  /** uid -> unread count, maintained server-side / by clients incrementally. */
  unread?: Record<string, number>;
}

/**
 * What automated moderation knows about a message — its text AND its
 * attachment, as one answer.
 *
 *  - 'clean'     — a moderation provider inspected everything in this message
 *                  and passed it.
 *  - 'unchecked' — something in it was not inspected: no provider configured,
 *                  the vendor timed out or errored, moderation was switched
 *                  off — or the attachment is a VIDEO, which nothing checks.
 *                  The message was still delivered (see the outage policy in
 *                  functions/src/safety/moderation.ts); this marker is what a
 *                  later sweep queries on.
 *
 * A message carrying a video is therefore ALWAYS 'unchecked', however clean
 * its text was. That is not a bug and must not be "fixed" by ignoring the
 * attachment: there is no video moderation in this product, and a status that
 * said 'clean' over an uninspected video would be a lie told by a field that
 * exists precisely so a safety sweep can trust it.
 *
 * There is deliberately no 'flagged' value. A message that fails moderation is
 * never written at all, so the flagged text does not exist anywhere in
 * Firestore — only the sender's own composer still holds it.
 *
 * Optional because messages written before moderation shipped have no value
 * here; treat a missing field as 'unchecked'.
 */
export type MessageModerationStatus = 'clean' | 'unchecked';

export interface MessageDoc {
  id: string;
  senderUid: string;
  text: string;
  sentAtISO: string;
  /** Set by the send callable. Absent on pre-moderation messages. */
  moderation?: MessageModerationStatus;
  /**
   * One attached photo or video. `text` may be empty when this is present —
   * a photo with no caption is a normal message.
   *
   * ATTACHING is Pro-only and VIEWING is Pro-only, and those are two separate
   * enforcement points: the first is the messageSend callable, the second is
   * storage.rules. Everything on MediaRef is safe for a non-Pro member to
   * read — dimensions, a byte count, a duration and an uninvertible ~28-char
   * blur — because the bytes behind `path` are the only thing withheld, and a
   * non-Pro client never requests them. It renders `blurhash` instead.
   *
   * `path` is an object path, NEVER a download URL. A download URL carries a
   * token that bypasses Storage rules entirely, so putting one here would hand
   * every non-Pro reader of this document exactly what the tier check exists
   * to withhold. See packages/shared/src/types/media.ts.
   */
  media?: MediaRef;
  /**
   * uids that could NOT open `media` at the moment this message was sent.
   *
   * WHY THIS IS PERSISTED RATHER THAN COMPUTED. A Pro member sending a photo
   * to a free member would otherwise get no signal at all: the send succeeds,
   * the bubble looks right on their screen, and the photo lands as a locked
   * blur that the other person never sees. A conversation where one side does
   * not know their photo was never looked at is worse than not allowing the
   * photo. The sender's client cannot read the other member's entitlement doc
   * (rules deny it), so the server answers the question once, at send time,
   * and stores the answer here for the sender's screen to render.
   *
   * IT IS A SNAPSHOT, NOT A LIVE FLAG, and the copy that renders it must be
   * written in the past tense: someone who subscribes tomorrow can open every
   * photo they were sent yesterday, because storage.rules is evaluated when the
   * object is fetched, not when it was linked.
   *
   * The disclosure is deliberate and narrow: it tells one connected member that
   * another is not a subscriber, which is the minimum needed to stop the silent
   * failure above.
   */
  mediaLockedFor?: string[];
  /** Delivery/read receipts: uids that received / read the message. */
  deliveredTo: string[];
  readBy: string[];
  /** uid → single emoji reaction. */
  reactions?: Record<string, string>;
  /** Id of the message this replies to. */
  replyTo?: string;
  /** Set when edited (allowed within a short window, sender only). */
  editedAtISO?: string;
  /** uids that deleted this message for themselves. */
  deletedFor?: string[];
}

/**
 * Machine-readable reason carried in the callable error's `details` when a
 * send is refused by moderation. The client keys its copy off this rather than
 * off the error message, so the wording can change in any language without
 * breaking the check. Both sides import this constant — it is the contract.
 */
export const MODERATION_BLOCKED_REASON = 'moderation-blocked';

/**
 * The other refusal: moderation could not reach a verdict AND the fleet is
 * configured fail-closed. Kept distinct from the one above because the two
 * mean opposite things to the person who wrote the message — "this breaks a
 * rule" versus "we couldn't check it yet, try again".
 */
export const MODERATION_UNAVAILABLE_REASON = 'moderation-unavailable';

/**
 * Runtime switches for user-to-user message moderation.
 *
 * These live in the Firestore doc `config/app` under a `moderation` key, so
 * the fail-open/fail-closed decision can be flipped for the whole fleet inside
 * one config-cache TTL (60s) without a deploy — see the OUTAGE POLICY comment
 * in functions/src/safety/moderation.ts.
 *
 * WHY IT IS READ RATHER THAN TYPED INTO AppConfigValues: mergeAppConfig()
 * spreads the remote document over the defaults, so a `moderation` section in
 * `config/app` already survives into the merged object at runtime — it simply
 * is not on the interface. Reading it through the tolerant parser below keeps
 * this change out of config/entitlements.ts. When that file is next opened,
 * promote `moderation: MessageModerationConfig` onto AppConfigValues and add
 * DEFAULT_MESSAGE_MODERATION_CONFIG to DEFAULT_APP_CONFIG; this parser then
 * becomes a straight property read and can be deleted.
 */
export interface MessageModerationConfig {
  /** False disables the vendor call entirely; everything becomes 'unchecked'. */
  enabled: boolean;
  /** True (default): a provider outage delivers the message as 'unchecked'. */
  failOpen: boolean;
  /** Deadline for one check. Messaging must not feel broken when the vendor is slow. */
  timeoutMs: number;
}

export const DEFAULT_MESSAGE_MODERATION_CONFIG: MessageModerationConfig = {
  enabled: true,
  failOpen: true,
  timeoutMs: 3000,
};

/** Clamp: a misconfigured value must not be able to hang the send path. */
const MIN_MODERATION_TIMEOUT_MS = 250;
const MAX_MODERATION_TIMEOUT_MS = 10_000;

/**
 * Pull the moderation section out of a merged app-config object, tolerating a
 * missing, partial or malformed `moderation` key — a bad hand-edit of
 * `config/app` must degrade to the defaults, never throw on the send path.
 */
export function readMessageModerationConfig(config: unknown): MessageModerationConfig {
  const raw = (config as { moderation?: unknown } | null | undefined)?.moderation;
  if (!raw || typeof raw !== 'object') return DEFAULT_MESSAGE_MODERATION_CONFIG;
  const o = raw as Record<string, unknown>;
  const d = DEFAULT_MESSAGE_MODERATION_CONFIG;
  const timeoutMs = typeof o.timeoutMs === 'number' && Number.isFinite(o.timeoutMs)
    ? Math.min(Math.max(Math.round(o.timeoutMs), MIN_MODERATION_TIMEOUT_MS), MAX_MODERATION_TIMEOUT_MS)
    : d.timeoutMs;
  return {
    enabled: typeof o.enabled === 'boolean' ? o.enabled : d.enabled,
    failOpen: typeof o.failOpen === 'boolean' ? o.failOpen : d.failOpen,
    timeoutMs,
  };
}

/**
 * AI assistance inside a human conversation is strictly user-triggered.
 * This record exists only when the user explicitly asked Ally for help there.
 */
export interface ConversationAssistRequest {
  conversationId: string;
  requestedByUid: string;
  kind: 'suggest-reply' | 'summarise-visible' | 'tone-check';
  createdAtISO: string;
}
